// Birim varyantı DR2 — gruplu birim seçicisinin saf seçenek mantığı (UnitSelect bunu render eder).
// Gruplar sabit sırada: Katalog varsayılanı → Tanımlı alternatifler → Mevcut birimden çevrilebilir
// (<mevcut>) → Diğer birim…; bir birim yalnız İLK uyduğu grupta görünür (normalize anahtarla
// tekil). Çevrilebilirlik MEVCUT etkin birime göre (`current ?? defaultUnit`): çevrim tablosundan
// ulaşılabilenler (`reachableUnits`) + aynı para biriminin ölçekleri (shared `currencyScaleUnits`
// — `reachableUnits` para listelemez, ölçek kur değildir). İpucu: "×0,001" | "otomatik kur çevrimi yok" |
// "mevcut" | null.

import {
  currencyBaseOf,
  currencyScaleUnits,
  normalizeUnitKey,
  reachableUnits,
  unitConversionFactor,
} from "@mahir/shared";
import { KPI_UNIT_COPY } from "./kpi-unit-copy";

export type UnitOptionGroupKey = "default" | "variants" | "convertible" | "other" | "family";

export interface UnitOption {
  value: string;
  label: string;
  hint: string | null;
}

export interface UnitOptionGroup {
  key: UnitOptionGroupKey;
  /** React anahtarı için tekil kimlik (birden çok "family" grubu); yoksa `key` kullanılır */
  id?: string;
  label: string;
  options: UnitOption[];
}

/** "Diğer birim…" seçeneğinin değeri (gerçek birim değil — metin alanını açar). */
export const OTHER_UNIT_VALUE = "__other__";

/** Türkçe sayı: binlik nokta, ondalık virgül; kesir basamağı kırpılmaz (0,0015). */
export function formatUnitNumber(v: number): string {
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 10 });
}

/** Çevrim çarpanı "×0,001" / "×1.000" / "×0,2778" (4 anlamlı basamak). */
export function formatUnitFactor(k: number): string {
  return `×${k.toLocaleString("tr-TR", { maximumSignificantDigits: 4 })}`;
}

function hintFor(current: string | null, unit: string): string | null {
  if (current === null || current.trim() === "") return null;
  if (normalizeUnitKey(current) === normalizeUnitKey(unit)) return KPI_UNIT_COPY.currentHint;
  const k = unitConversionFactor(current, unit);
  if (k !== null) return k === 1 ? null : formatUnitFactor(k);
  if (currencyBaseOf(current) !== null && currencyBaseOf(unit) !== null) {
    return KPI_UNIT_COPY.noCurrency;
  }
  return null;
}

export function unitOptionsFor(args: {
  defaultUnit: string | null;
  variants: readonly string[];
  current: string | null;
}): UnitOptionGroup[] {
  const clean = (u: string | null): string | null => {
    const t = u?.trim() ?? "";
    return t === "" ? null : t;
  };
  const defaultUnit = clean(args.defaultUnit);
  const current = clean(args.current) ?? defaultUnit;
  const seen = new Set<string>();
  const take = (units: readonly string[]): UnitOption[] => {
    const out: UnitOption[] = [];
    for (const raw of units) {
      const u = raw.trim();
      if (u === "") continue;
      const key = normalizeUnitKey(u);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: u, label: u, hint: hintFor(current, u) });
    }
    return out;
  };

  const groups: UnitOptionGroup[] = [];
  const defaultOptions: UnitOption[] = defaultUnit
    ? take([defaultUnit])
    : [{ value: "", label: KPI_UNIT_COPY.unitless, hint: null }];
  groups.push({ key: "default", label: KPI_UNIT_COPY.groups.default, options: defaultOptions });

  const variants = take(args.variants);
  if (variants.length > 0) {
    groups.push({ key: "variants", label: KPI_UNIT_COPY.groups.variants, options: variants });
  }

  if (current) {
    // mevcut birim listede yoksa (ör. müşteri "Bin TL" seçmiş) grubun başında görünür — seçili
    // değer listbox'ta her zaman bulunur
    const convertible = take([current, ...reachableUnits(current), ...currencyScaleUnits(current)]);
    if (convertible.length > 0) {
      groups.push({
        key: "convertible",
        label: KPI_UNIT_COPY.groups.convertible(current),
        options: convertible,
      });
    }
  }

  groups.push({
    key: "other",
    label: KPI_UNIT_COPY.groups.other,
    options: [{ value: OTHER_UNIT_VALUE, label: KPI_UNIT_COPY.groups.other, hint: null }],
  });
  return groups;
}
